// M8 已切真后端（/api/reports）— 真实生成 API 在 M9 上线
// 数据报告:生成本周周报 / 本月月报 + 历史归档
// 对齐 WeChat MP 「数据报告」:卡左上 type tag(微信绿/微信蓝/中性灰) + 右上日期
// + 卡内 KPI 4 个小数字横排 + 底部「查看/下载/删除」
import { useState } from 'react'
import { Button, Card, Tag, App, Empty } from 'antd'
import { FilePlus, Download, Eye, FileBarChart2, Trash2 } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { useReportList, type ReportType } from '@/mocks/reports'
import { timeAgo } from '@/lib/utils'

function typeTag(type: ReportType) {
  // 周报:微信绿(主色);月报:微信蓝(链接);年报:中性灰
  if (type === 'weekly') return { color: 'success', text: '周报' }
  if (type === 'monthly') return { color: 'processing', text: '月报' }
  return { color: 'default', text: '年报' }
}

export default function Reports() {
  const { message } = App.useApp()
  const { data, isLoading } = useReportList()
  const [generating, setGenerating] = useState<ReportType | null>(null)

  const handleGenerate = async (type: ReportType) => {
    setGenerating(type)
    await new Promise((r) => setTimeout(r, 600))
    setGenerating(null)
    message.warning(`M9 报告生成器尚未上线 — ${type === 'weekly' ? '周报' : type === 'monthly' ? '月报' : '年报'}生成 API 准备好后再启用`)
  }

  return (
    <div>
      <PageHeader
        title="数据报告"
        subtitle="自动生成的周报、月报和年报"
        extra={
          <div className="flex items-center gap-2">
            <Button
              type="primary"
              icon={<FilePlus size={14} />}
              loading={generating === 'weekly'}
              onClick={() => handleGenerate('weekly')}
            >
              生成本周周报
            </Button>
            <Button
              icon={<FileBarChart2 size={14} />}
              loading={generating === 'monthly'}
              onClick={() => handleGenerate('monthly')}
            >
              生成本月月报
            </Button>
          </div>
        }
      />

      {/* 历史报告 */}
      <div className="mb-3 text-sm text-ink-500 font-medium">历史报告</div>
      {isLoading ? (
        <div className="text-sm text-ink-300 py-8 text-center">加载中…</div>
      ) : !data || data.length === 0 ? (
        <Empty description="暂无历史报告" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((r) => {
            const tag = typeTag(r.type)
            return (
              <Card key={r.id} size="small" hoverable className="transition-colors">
                {/* 头:type tag(左上)+ 日期(右上) */}
                <div className="flex items-start justify-between mb-2">
                  <Tag color={tag.color} className="!mr-0">{tag.text}</Tag>
                  <span className="text-xs text-ink-300 num" title={r.generatedAt ? new Date(r.generatedAt).toLocaleString('zh-CN') : ''}>
                    {r.generatedAt ? timeAgo(r.generatedAt) : '—'}
                  </span>
                </div>

                <h3 className="text-base font-semibold text-ink-900 mb-1 truncate" title={r.title}>
                  {r.title}
                </h3>
                <p className="text-xs text-ink-500 mb-3 num">
                  {r.periodStart} ~ {r.periodEnd}
                </p>

                {/* KPI 4 个小数字横排 — 报告归档暂无 KPI 字段(M9 报告生成器上线后填充) */}
                <div className="grid grid-cols-4 gap-2 mb-3 py-3 border-y border-ink-100">
                  <div>
                    <div className="text-xs text-ink-500">阅读</div>
                    <div className="text-sm num font-semibold text-ink-300">—</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">分享</div>
                    <div className="text-sm num font-semibold text-ink-300">—</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">留言</div>
                    <div className="text-sm num font-semibold text-ink-300">—</div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-500">净增</div>
                    <div className="text-sm num font-semibold text-ink-300">—</div>
                  </div>
                </div>

                <ul className="text-xs text-ink-700 space-y-0.5 mb-3 min-h-[40px]">
                  {r.highlights.length > 0 ? (
                    r.highlights.slice(0, 3).map((h, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-primary-600 flex-shrink-0">·</span>
                        <span className="line-clamp-1">{h}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-ink-300">暂无摘要（M9 报告生成器上线后填充）</li>
                  )}
                </ul>

                <div className="flex items-center justify-between pt-2 border-t border-ink-100">
                  <span className="text-xs text-ink-300 num">
                    {r.size || '—'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="text"
                      size="small"
                      icon={<Eye size={12} />}
                      onClick={() => message.info(`查看报告 ${r.title}(M9 接入)`)}
                    >
                      查看
                    </Button>
                    {r.pdfPath && (
                      <Button
                        type="text"
                        size="small"
                        icon={<Download size={12} />}
                        onClick={() => window.open(r.pdfPath ?? '#', '_blank')}
                      >
                        下载
                      </Button>
                    )}
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={12} />}
                      onClick={() => message.warning(`删除报告 ${r.title}(M9 接入)`)}
                    />
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
